import GroupChatService from '../../services/GroupChatService';
import GroupChatHub from '../../services/GroupChatHub';
import { IChatMessage } from '../../components/GroupChat.vue';

export interface GroupChatState {
  messages: IChatMessage[];
  loading: boolean;
  loadingOlder: boolean;
  sending: boolean;
  hasMoreMessages: boolean;
  groupName: string;
  someoneTyping: boolean;
}

let typingClearTimeout: ReturnType<typeof setTimeout> | null = null;

export default {
  state: {
    messages: [] as IChatMessage[],
    loading: false,
    loadingOlder: false,
    sending: false,
    hasMoreMessages: true,
    groupName: '',
    someoneTyping: false,
  } as GroupChatState,

  mutations: {
    SET_CHAT_MESSAGES(state: GroupChatState, messages: IChatMessage[]) {
      state.messages = messages;
    },
    PREPEND_CHAT_MESSAGES(state: GroupChatState, messages: IChatMessage[]) {
      state.messages = [...messages, ...state.messages];
    },
    APPEND_CHAT_MESSAGE(state: GroupChatState, message: IChatMessage) {
      // Dedup — the sender already has the message from the POST response.
      if (state.messages.some((m) => m.id === message.id)) return;
      state.messages = [...state.messages, message];
    },
    REMOVE_CHAT_MESSAGE(state: GroupChatState, messageId: number) {
      state.messages = state.messages.filter((m) => m.id !== messageId);
    },
    SET_CHAT_LOADING(state: GroupChatState, val: boolean) {
      state.loading = val;
    },
    SET_CHAT_LOADING_OLDER(state: GroupChatState, val: boolean) {
      state.loadingOlder = val;
    },
    SET_CHAT_SENDING(state: GroupChatState, val: boolean) {
      state.sending = val;
    },
    SET_CHAT_HAS_MORE(state: GroupChatState, val: boolean) {
      state.hasMoreMessages = val;
    },
    SET_GROUP_NAME(state: GroupChatState, name: string) {
      state.groupName = name;
    },
    SET_SOMEONE_TYPING(state: GroupChatState, val: boolean) {
      state.someoneTyping = val;
    },
  },

  actions: {
    async fetchChatMessages(context: any) {
      context.commit('SET_CHAT_LOADING', true);
      try {
        const data = await GroupChatService.getMessages();
        context.commit('SET_CHAT_MESSAGES', data.messages);
        context.commit('SET_CHAT_HAS_MORE', data.messages.length >= 20);
        context.commit('SET_GROUP_NAME', data.groupName);
      } catch (e) {
        console.error('[GroupChatStore] Failed to load messages:', e);
      } finally {
        context.commit('SET_CHAT_LOADING', false);
      }
    },

    async loadOlderChatMessages(context: any) {
      const state: GroupChatState = context.state;
      if (
        state.loadingOlder ||
        !state.hasMoreMessages ||
        state.messages.length === 0
      )
        return;

      const oldestId = state.messages[0].id;
      context.commit('SET_CHAT_LOADING_OLDER', true);
      try {
        const data = await GroupChatService.getMessages(oldestId);
        context.commit('SET_CHAT_HAS_MORE', data.messages.length >= 20);
        context.commit('PREPEND_CHAT_MESSAGES', data.messages);
      } catch (e) {
        console.error('[GroupChatStore] Failed to load older messages:', e);
      } finally {
        context.commit('SET_CHAT_LOADING_OLDER', false);
      }
    },

    async sendChatMessage(context: any, text: string) {
      context.commit('SET_CHAT_SENDING', true);
      try {
        const saved = await GroupChatService.postMessage(text);
        context.commit('APPEND_CHAT_MESSAGE', saved);
      } catch (e) {
        console.error('[GroupChatStore] Failed to send message:', e);
      } finally {
        context.commit('SET_CHAT_SENDING', false);
      }
    },

    async deleteChatMessage(context: any, id: number) {
      try {
        await GroupChatService.deleteMessage(id);
        context.commit('REMOVE_CHAT_MESSAGE', id);
      } catch (e) {
        console.error('[GroupChatStore] Failed to delete message:', e);
      }
    },

    // Dispatched by Hubs.ts when a ReceiveChatMessage SignalR event fires.
    chatMessageReceived(context: any, msg: IChatMessage) {
      context.commit('APPEND_CHAT_MESSAGE', msg);
    },

    // Dispatched by Hubs.ts when a ReceiveChatMessageDeleted SignalR event fires.
    chatMessageDeleted(context: any, messageId: number) {
      context.commit('REMOVE_CHAT_MESSAGE', messageId);
    },

    // Dispatched by Hubs.ts when a ReceiveTypingIndicator event fires.
    chatTypingReceived(context: any) {
      context.commit('SET_SOMEONE_TYPING', true);
      if (typingClearTimeout) clearTimeout(typingClearTimeout);
      typingClearTimeout = setTimeout(() => {
        context.commit('SET_SOMEONE_TYPING', false);
        typingClearTimeout = null;
      }, 3000);
    },

    sendChatTyping() {
      GroupChatHub.sendTyping();
    },
  },
};
